// Angular Import
import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule, Event } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

// project import
import { getNavigationItems, NavigationItem } from 'src/app/theme/layout/admin/navigation/navigation';
import { SharedModule } from '../../shared.module';
import { UsersService } from 'src/app/dashboard/users/users.service';
import { DeviceConstantsService } from 'src/app/dashboard/devices/device-constants.service';
import { filter, Subscription } from 'rxjs';

interface titleType {
	// eslint-disable-next-line
	url: string | boolean | any | undefined;
	title: string;
	breadcrumbs: unknown;
	type: string;
}

@Component({
    selector: 'app-breadcrumb',
    imports: [CommonModule, RouterModule, SharedModule],
    templateUrl: './breadcrumb.component.html'
})
export class BreadcrumbComponent {
	// public props
	@Input() type: string;

	navigations: NavigationItem[];
	breadcrumbList: Array<string> = [];
	navigationList!: titleType[];
	/**
	 * Resolved once rather than called from the template: a fresh array on every change-detection
	 * pass makes RouterLink rebuild its URL tree each time. The signed-in user cannot change
	 * without reloading this shell, so one lookup is enough.
	 */
	homeRoute!: string[];
    private routerSubscription!: Subscription;

	// constructor
	constructor(
		private route: Router,
		private titleService: Title,
		private translate: TranslateService,
		public userService: UsersService,
		private deviceConstants: DeviceConstantsService
	) {
		this.navigations = getNavigationItems();
		this.homeRoute = this.userService.homeRoute();
		this.type = 'icon';
		this.setBreadcrumb();
	}

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }


    matchUrl(navItem: NavigationItem, activeLink: string): boolean {
        if ('url' in navItem) return navItem.url === activeLink;
        
        if ('urlPattern' in navItem) {
            const lastSegment = activeLink.substring(activeLink.lastIndexOf('/') + 1);
            
            if (navItem.urlPattern === lastSegment) {
                return true;
            }
            
            return new RegExp(navItem.urlPattern).test(activeLink);
        }
        return false;
    }




    setBreadcrumb() {
        this.routerSubscription = this.route.events.pipe(
            filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
        ).subscribe((router: NavigationEnd) => {
            // urlAfterRedirects, not url: opening a device lands on `/device-page/<stage>/<imei>`
            // and the router redirects that to its default tab. `url` still holds the pre-redirect
            // address, so reading it would lose the tab level (VMSPRO-573).
            const activeLink = router.urlAfterRedirects.split('?')[0].split('#')[0];

            // The devices area cannot come from the nav tree — its stage and IMEI are URL
            // segments, not static config — so it gets its own builder (VMSPRO-573).
            const breadcrumbList = this.buildDeviceBreadcrumb(activeLink)
                ?? this.filterNavigation(this.navigations, activeLink);

            const title = breadcrumbList[breadcrumbList.length - 1]?.title;
            
            this.navigationList = breadcrumbList; 

            if (title)
                this.titleService.setTitle(`${this.translate.instant('g.vms')} - ${this.translate.instant(title)}`);
        });
    }

    /**
     * Breadcrumb for the devices area, where the trail cannot be read off `navigation.ts`: the
     * category and the IMEI are URL segments, not static nav entries.
     *
     * Covers `/devices/<stage>` and `/device-page/<stage>/<imei>[/<tab>]`. Returns null for
     * anything else — including the legacy `/device-page/<imei>/<tab>` URL, whose first segment is
     * not a category — so the caller falls back to the ordinary nav walk.
     */
    private buildDeviceBreadcrumb(activeLink: string): titleType[] | null {
        const segments = activeLink.split('/').filter(segment => segment.length > 0);

        if (segments[0] === 'devices' && segments.length === 2) {
            const stage = this.deviceConstants.findStageBySlug(segments[1]);
            if (!stage)
                return null;

            return [
                this.devicesListCrumb(),
                { url: false, title: stage.label, breadcrumbs: true, type: 'item' }
            ];
        }

        if (segments[0] === 'device-page' && segments.length >= 3) {
            const stage = this.deviceConstants.findStageBySlug(segments[1]);
            if (!stage)
                return null;

            const crumbs: titleType[] = [
                this.devicesListCrumb(),
                { url: `/devices/${segments[1]}`, title: stage.label, breadcrumbs: true, type: 'item' },
                // Deliberately not a link: every device URL redirects to a tab, so the link would
                // only ever point at the page the user is already on (VMSPRO-573).
                { url: false, title: segments[2], breadcrumbs: true, type: 'item' }
            ];

            // A tab with no nav entry yet simply ends the trail at the IMEI, rather than dropping
            // the whole breadcrumb — and with it the page title — the way an unmatched URL does.
            const tab = this.findDeviceTab(segments[3]);
            if (tab)
                crumbs.push({ url: false, title: tab.title, breadcrumbs: true, type: 'item' });

            return crumbs;
        }

        return null;
    }

    /** `/devices` redirects to the first category tab, which is where this link is meant to land. */
    private devicesListCrumb(): titleType {
        return { url: '/devices', title: 'nav.devices_list', breadcrumbs: true, type: 'item' };
    }

    /** The device-page tab whose nav entry matches this URL segment, if it has one. */
    private findDeviceTab(slug: string | undefined): NavigationItem | undefined {
        if (!slug)
            return undefined;

        const devicePage = this.navigations
            .flatMap(group => group.children ?? [])
            .find(item => item.id === 'device-page');

        // The tab patterns are anchored with a trailing `$` so that a short one cannot match a
        // longer URL — `define` matching `/define-log`, say. Drop the anchor to compare segments.
        return devicePage?.children?.find(child => child.urlPattern?.replace(/\$$/, '') === slug);
    }

    filterNavigation(navItems: NavigationItem[], activeLink: string): titleType[] {
        for (const navItem of navItems) {
            
            if (navItem.type === 'item' && this.matchUrl(navItem, activeLink)) {
                return [
                    {
                        url: false,
                        title: navItem.title, 
                        breadcrumbs: 'breadcrumbs' in navItem ? navItem.breadcrumbs : true,
                        type: navItem.type
                    }
                ];
            }
            
            if ('children' in navItem) {
                const breadcrumbList = this.filterNavigation(navItem.children!, activeLink);

                if (breadcrumbList.length > 0) {
                    breadcrumbList.unshift({
                        url: 'url' in navItem ? navItem.url : false,
                        title: navItem.title,
                        breadcrumbs: 'breadcrumbs' in navItem ? navItem.breadcrumbs : true,
                        type: navItem.type
                    });
                    return breadcrumbList;
                }
            }
        }
        return [];
    }
}
